import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { NotificationType } from 'angular2-notifications'
import { catchError, map, of, switchMap } from 'rxjs'
import { BasicResult } from '../../models/interfaces/basic-result.interface'
import { CRMOrganization, CRMOrganizationRequest } from '../../models/interfaces/crm-organization.interface'
import { NotificationService } from '../../services/notification.service'
import * as StageActions from './stage.actions'
import { StageService } from './stage.service'

@Injectable()
export class StageEffects {
   private _actions$ = inject(Actions)
   private _stageService = inject(StageService)
   private _notificationService = inject(NotificationService)

   searchCRMOrganizationList$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.searchCRMOrganizationList),
         map((action) => action.query),
         switchMap((query: string) =>
            this._stageService.searchOrganizationsList(query).pipe(
               map((response: BasicResult<CRMOrganizationRequest, CRMOrganization>) =>
                  StageActions.searchCRMOrganizationListSuccess({ response: response.results })
               ),
               catchError((error) => of(StageActions.searchCRMOrganizationListFailure({ error })))
            )
         )
      )
   )

   loadSectors$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setSectors),
         switchMap(() =>
            this._stageService.getSectors().pipe(
               map((sectors) => StageActions.setSectorsSuccess({ sectors })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setSectorsFailure({ error: error }))
               })
            )
         )
      )
   )

   loadSoughtPartnerCountries$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setSoughtPartnerCountries),
         switchMap(() =>
            this._stageService.getSoughtPartnerCountries().pipe(
               map((soughtPartnerCountries) => StageActions.setSoughtPartnerCountriesSuccess({ soughtPartnerCountries })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setSoughtPartnerCountriesFailure({ error: error }))
               })
            )
         )
      )
   )

   loadPNOUserNotifications$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.getPNOUserNotifications),
         switchMap(() =>
            this._stageService.getPNOUserNotifications().pipe(
               map((userNotifications) => StageActions.getPNOUserNotificationsSuccess({ userNotifications })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.getPNOUserNotificationsFailure({ error: error }))
               })
            )
         )
      )
   )

   updatePNOUserNotifications$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.updatePNOUserNotifications),
         switchMap((action) =>
            this._stageService.updatePNOUserNotifications(action.notificationName, action.isActive).pipe(
               map(() => StageActions.updatePNOUserNotificationsSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.updatePNOUserNotificationsFailure({ error: error }))
               })
            )
         )
      )
   )

   private _actionsFailedNotification = [
      StageActions.searchCRMOrganizationListFailure,
      StageActions.setSectorsFailure,
      StageActions.setSoughtPartnerCountriesFailure,
      StageActions.getPNOUserNotificationsFailure,
      StageActions.updatePNOUserNotificationsFailure,
   ]

   listenAllFailedAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsFailedNotification),
            map((errorResponse) => this._notificationService.notify(NotificationType.Error, undefined, errorResponse.error.message))
         ),
      { dispatch: false }
   )

   /* Notification success message */
   private _actionsForSuccessNotificationMessage = [StageActions.updatePNOUserNotificationsSuccess]

   listenAllSuccessAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForSuccessNotificationMessage),
            map(() => this._notificationService.notify(NotificationType.Success))
         ),
      { dispatch: false }
   )
}
